import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { usePreferences } from '../contexts/PreferencesContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

// ── TypeScript Interfaces ─────────────────────────────────────

type MembershipStatus = 'Active' | 'Inactive' | 'Suspended';
type PaymentMethod = 'Cash' | 'Check' | 'CreditCard' | 'BankTransfer' | 'PayPal' | 'Zelle' | 'Other';
type SubcommitteeRole = 'Chair' | 'Member' | 'Volunteer';

interface SubcommitteeAffiliation {
  subcommitteeId: string;
  subcommitteeName: string;
  role: SubcommitteeRole;
}

interface ExecutiveTitle {
  id: string;
  name: string;
  sortOrder: number;
}

interface MembershipLevel {
  id: string;
  name: string;
  description: string;
  sortOrder: number;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  mailingAddress: string | null;
  chapterAffiliation: string | null;
  joinDate: string;
  status: MembershipStatus;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
  subcommitteeAffiliations: SubcommitteeAffiliation[];
  paidStatus: boolean;
  isExecutive: boolean;
  executiveTitleId: string | null;
  membershipLevelId: string | null;
}

interface SubcommitteeOption {
  id: string;
  name: string;
}

interface MemberDetail extends Member {
  user: { id: string; fullName: string; email: string } | null;
  duesPayments: DuesPayment[];
  fundraisingLinks: FundraisingLink[];
  duesStatus: DuesStatus | null;
  executiveTitle: { id: string; name: string } | null;
  membershipLevel: { id: string; name: string } | null;
}

interface DuesStatus {
  periodId: string;
  year: number;
  annualAmount: number;
  totalPaid: number;
  remainingBalance: number;
  paidInFull: boolean;
}

interface DuesPayment {
  id: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  duesPeriod: { id: string; year: number; annualAmount: number };
}

interface FundraisingLink {
  id: string;
  name: string;
  type: string;
  associationDate: string;
}

interface DuesPeriod {
  id: string;
  year: number;
  annualAmount: number;
}

interface DuesSummary {
  totalMembers: number;
  paidCount: number;
  unpaidCount: number;
  totalCollected: number;
  expectedTotal: number;
  progressPercent: number;
  hasDuesPeriod: boolean;
}

interface FundraisingActivity {
  id: string;
  name: string;
  type: string;
}

interface UserOption {
  id: string;
  fullName: string;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnSecondary: { padding: '0.5rem 1rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  trClickable: { cursor: 'pointer', transition: 'background-color 0.15s' },
  trExpanded: { backgroundColor: '#f0f7f2' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  summaryCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' },
  summaryCard: { padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0', textAlign: 'center' as const },
  summaryValue: { fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' },
  summaryLabel: { fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' },
  progressContainer: { marginTop: '1rem' },
  progressBar: { width: '100%', height: '24px', backgroundColor: '#e0e0e0', borderRadius: '12px', overflow: 'hidden', position: 'relative' as const },
  progressFill: { height: '100%', borderRadius: '12px', transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.8rem', fontWeight: 600 },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: '1rem' },
  card: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa', cursor: 'pointer', transition: 'background-color 0.15s' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' },
  detailPanel: { padding: '1.5rem', backgroundColor: '#f9fdf9', border: '1px solid #c9e8d4', borderRadius: '6px', marginTop: '0.5rem', marginBottom: '0.75rem' },
  detailSection: { marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0' },
  detailLabel: { fontWeight: 600, fontSize: '0.8rem', color: '#666', marginBottom: '0.15rem' },
  detailValue: { fontSize: '0.9rem', color: '#333' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  paymentItem: { padding: '0.5rem 0.75rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', marginBottom: '0.5rem', fontSize: '0.85rem' },
  dropdownContainer: { position: 'relative' as const, width: '100%' },
  dropdown: { position: 'absolute' as const, top: '100%', left: 0, right: 0, maxHeight: '200px', overflowY: 'auto' as const, backgroundColor: '#fff', border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  dropdownOption: { padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0' },
  dropdownOptionHover: { backgroundColor: '#f0f7f2' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
};

const STATUS_COLORS: Record<MembershipStatus, string> = {
  Active: '#1a6b3c',
  Inactive: '#666',
  Suspended: '#d32f2f',
};

const ROLE_COLORS: Record<SubcommitteeRole, string> = {
  Chair: '#c9a84c',
  Member: '#1a6b3c',
  Volunteer: '#4a90d9',
};

const PAYMENT_METHODS: PaymentMethod[] = ['Cash', 'Check', 'CreditCard', 'BankTransfer', 'PayPal', 'Zelle', 'Other'];

// ── Helper Functions ──────────────────────────────────────────

function computeDuesSummary(members: Member[], duesPeriods: DuesPeriod[]): DuesSummary {
  const currentYear = new Date().getFullYear();
  const currentPeriod = duesPeriods.find(p => p.year === currentYear);

  if (!currentPeriod) {
    return {
      totalMembers: members.length,
      paidCount: 0,
      unpaidCount: 0,
      totalCollected: 0,
      expectedTotal: 0,
      progressPercent: 0,
      hasDuesPeriod: false,
    };
  }

  const expectedTotal = members.length * currentPeriod.annualAmount;

  return {
    totalMembers: members.length,
    paidCount: 0,
    unpaidCount: members.length,
    totalCollected: 0,
    expectedTotal,
    progressPercent: 0,
    hasDuesPeriod: true,
  };
}

function filterMembers(members: Member[], searchText: string, statusFilter: string, chapterFilter: string, subcommitteeFilter: string, executiveFilter: string, membershipLevelFilter: string): Member[] {
  return members.filter(member => {
    const matchesSearch = !searchText ||
      member.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
      member.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
      member.email.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus = !statusFilter || member.status === statusFilter;
    const matchesChapter = !chapterFilter || member.chapterAffiliation === chapterFilter;
    const matchesSubcommittee = !subcommitteeFilter || member.subcommitteeAffiliations.some(a => a.subcommitteeId === subcommitteeFilter);
    const matchesExecutive = !executiveFilter || (executiveFilter === 'yes' ? member.isExecutive : !member.isExecutive);
    const matchesMembershipLevel = !membershipLevelFilter || member.membershipLevelId === membershipLevelFilter;

    return matchesSearch && matchesStatus && matchesChapter && matchesSubcommittee && matchesExecutive && matchesMembershipLevel;
  });
}

function sortMembers(members: Member[]): Member[] {
  return [...members].sort((a, b) =>
    a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
  );
}


// ── UserSearchDropdown (inline) ───────────────────────────────

function UserSearchDropdown({ onSelect }: { onSelect: (userId: string, fullName: string) => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<UserOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchUsers = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const users = await apiClient<UserOption[]>(
        `/users/members?search=${encodeURIComponent(query)}`
      );
      setOptions(users);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(query), 300);
  };

  const handleSelect = (user: UserOption) => {
    setSearchQuery('');
    setIsOpen(false);
    onSelect(user.id, user.fullName);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div ref={containerRef} style={styles.dropdownContainer}>
      <input
        type="text"
        style={styles.input}
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={() => { setIsOpen(true); if (options.length === 0) fetchUsers(''); }}
        placeholder="Search users to link..."
        aria-label="Search users"
      />
      {isOpen && (
        <div style={styles.dropdown}>
          {loading && <div style={{ padding: '0.5rem', color: '#666', textAlign: 'center' }}>Loading...</div>}
          {!loading && options.length === 0 && <div style={{ padding: '0.5rem', color: '#666', textAlign: 'center' }}>No users found</div>}
          {!loading && options.map((user, idx) => (
            <div
              key={user.id}
              style={{ ...styles.dropdownOption, ...(idx === highlightedIndex ? styles.dropdownOptionHover : {}) }}
              onClick={() => handleSelect(user)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {user.fullName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Main Component ────────────────────────────────────────────

export function MembersPage() {
  const { user } = useAuth();
  const { canEdit } = usePageAuthorization('members');
  const { preferences } = usePreferences();

  // Data state
  const [members, setMembers] = useState<Member[]>([]);
  const [duesPeriods, setDuesPeriods] = useState<DuesPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // View state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() =>
    preferences.defaultViewMode === 'table' ? 'table' : 'cards'
  );
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [memberDetail, setMemberDetail] = useState<MemberDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<MembershipStatus | ''>('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [subcommitteeFilter, setSubcommitteeFilter] = useState('');
  const [executiveFilter, setExecutiveFilter] = useState<'' | 'yes' | 'no'>('');
  const [membershipLevelFilter, setMembershipLevelFilter] = useState('');

  // Registration form state
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('770-000-0000');
  const [regAddress, setRegAddress] = useState('');
  const [regChapter, setRegChapter] = useState('Georgia');
  const [regStatus, setRegStatus] = useState<MembershipStatus>('Active');
  const [regIsExecutive, setRegIsExecutive] = useState(false);
  const [regExecutiveTitleId, setRegExecutiveTitleId] = useState('');
  const [regMembershipLevelId, setRegMembershipLevelId] = useState('');
  const [regErrors, setRegErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Auto-generate email from first + last name
  useEffect(() => {
    if (regFirstName || regLastName) {
      const first = regFirstName.toLowerCase().replace(/\s+/g, '');
      const last = regLastName.toLowerCase().replace(/\s+/g, '');
      setRegEmail(`${first}.${last}@kdu-georgia.org`);
    }
  }, [regFirstName, regLastName]);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editChapter, setEditChapter] = useState('');
  const [editStatus, setEditStatus] = useState<MembershipStatus>('Active');
  const [editIsExecutive, setEditIsExecutive] = useState(false);
  const [editExecutiveTitleId, setEditExecutiveTitleId] = useState<string | null>(null);
  const [editMembershipLevelId, setEditMembershipLevelId] = useState<string | null>(null);

  // Reference data for dropdowns
  const [executiveTitles, setExecutiveTitles] = useState<ExecutiveTitle[]>([]);
  const [membershipLevels, setMembershipLevels] = useState<MembershipLevel[]>([]);

  // Dues payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('Cash');
  const [payPeriodId, setPayPeriodId] = useState('');

  // Fundraising state
  const [fundraisingActivities, setFundraisingActivities] = useState<FundraisingActivity[]>([]);
  const [showLinkActivity, setShowLinkActivity] = useState(false);

  // Affiliation management state
  const [availableSubcommittees, setAvailableSubcommittees] = useState<SubcommitteeOption[]>([]);
  const [newAffSubcommitteeId, setNewAffSubcommitteeId] = useState('');
  const [newAffRole, setNewAffRole] = useState<SubcommitteeRole>('Member');

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchData();
    fetchReferenceData();
  }, []);

  async function fetchReferenceData() {
    try {
      const [titles, levels] = await Promise.all([
        apiClient<ExecutiveTitle[]>('/executive-titles'),
        apiClient<MembershipLevel[]>('/membership-levels'),
      ]);
      setExecutiveTitles(titles);
      setMembershipLevels(levels);
    } catch {
      // Reference data fetch failure is non-critical; dropdowns will be empty
    }
  }

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const [membersData, periodsData] = await Promise.all([
        apiClient<Member[]>('/members'),
        apiClient<DuesPeriod[]>('/dues-periods'),
      ]);
      setMembers(membersData);
      setDuesPeriods(periodsData);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load members data.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchMemberDetail(id: string) {
    setDetailLoading(true);
    try {
      const detail = await apiClient<MemberDetail>(`/members/${id}`);
      setMemberDetail(detail);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load member details.');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleExpandMember(id: string) {
    if (expandedMemberId === id) {
      setExpandedMemberId(null);
      setMemberDetail(null);
      setEditMode(false);
      setShowPaymentForm(false);
      setShowLinkActivity(false);
    } else {
      setExpandedMemberId(id);
      setEditMode(false);
      setShowPaymentForm(false);
      setShowLinkActivity(false);
      fetchMemberDetail(id);
    }
  }

  // ── Registration ──────────────────────────────────────────

  function resetRegistrationForm() {
    setRegFirstName('');
    setRegLastName('');
    setRegEmail('');
    setRegPhone('');
    setRegAddress('');
    setRegChapter('');
    setRegStatus('Active');
    setRegIsExecutive(false);
    setRegExecutiveTitleId('');
    setRegMembershipLevelId('');
    setRegErrors([]);
  }

  async function handleRegisterMember(e: React.FormEvent) {
    e.preventDefault();
    const errors: string[] = [];
    if (!regFirstName.trim()) errors.push('First name is required');
    if (!regLastName.trim()) errors.push('Last name is required');
    if (!regEmail.trim()) errors.push('Email is required');
    if (errors.length > 0) { setRegErrors(errors); return; }

    setSubmitting(true);
    setError('');
    try {
      await apiClient('/members', {
        method: 'POST',
        body: {
          firstName: regFirstName.trim(),
          lastName: regLastName.trim(),
          email: regEmail.trim(),
          phone: regPhone.trim() || undefined,
          mailingAddress: regAddress.trim() || undefined,
          chapterAffiliation: regChapter.trim() || undefined,
          status: regStatus,
          isExecutive: regIsExecutive,
          executiveTitleId: regIsExecutive && regExecutiveTitleId ? regExecutiveTitleId : undefined,
          membershipLevelId: regMembershipLevelId || undefined,
        },
      });
      resetRegistrationForm();
      setShowRegistrationForm(false);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('A member with this email already exists');
      } else {
        setError(apiErr.message || 'Failed to create member.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit Mode ─────────────────────────────────────────────

  function enterEditMode() {
    if (!memberDetail) return;
    setEditFirstName(memberDetail.firstName);
    setEditLastName(memberDetail.lastName);
    setEditEmail(memberDetail.email);
    setEditPhone(memberDetail.phone || '');
    setEditAddress(memberDetail.mailingAddress || '');
    setEditChapter(memberDetail.chapterAffiliation || '');
    setEditStatus(memberDetail.status);
    setEditIsExecutive(memberDetail.isExecutive);
    setEditExecutiveTitleId(memberDetail.executiveTitleId);
    setEditMembershipLevelId(memberDetail.membershipLevelId);
    setEditMode(true);
  }

  function cancelEditMode() {
    setEditMode(false);
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}`, {
        method: 'PATCH',
        body: {
          firstName: editFirstName.trim(),
          lastName: editLastName.trim(),
          email: editEmail.trim(),
          phone: editPhone.trim() || null,
          mailingAddress: editAddress.trim() || null,
          chapterAffiliation: editChapter.trim() || null,
          status: editStatus,
          isExecutive: editIsExecutive,
          executiveTitleId: editIsExecutive ? editExecutiveTitleId : null,
          membershipLevelId: editMembershipLevelId,
        },
      });
      setEditMode(false);
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('A member with this email already exists');
      } else {
        setError(apiErr.message || 'Failed to update member.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Dues Payment ──────────────────────────────────────────

  async function handleRecordPayment(e: React.FormEvent) {
    e.preventDefault();
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/dues-payments`, {
        method: 'POST',
        body: {
          amount: parseFloat(payAmount),
          paymentDate: payDate,
          paymentMethod: payMethod,
          duesPeriodId: payPeriodId,
        },
      });
      setShowPaymentForm(false);
      setPayAmount('');
      setPayDate('');
      setPayMethod('Cash');
      setPayPeriodId('');
      await fetchMemberDetail(memberDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to record payment.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── User Linking ──────────────────────────────────────────

  async function handleLinkUser(userId: string) {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/link-user`, {
        method: 'POST',
        body: { userId },
      });
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('User is already linked to another member');
      } else {
        setError(apiErr.message || 'Failed to link user.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUnlinkUser() {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/unlink-user`, {
        method: 'POST',
      });
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to unlink user.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSyncUser() {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/sync-user`, {
        method: 'POST',
      });
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to sync user from member.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Fundraising ───────────────────────────────────────────

  async function handleShowLinkActivity() {
    setShowLinkActivity(true);
    try {
      const activities = await apiClient<FundraisingActivity[]>('/fundraising/activities');
      setFundraisingActivities(activities);
    } catch {
      setFundraisingActivities([]);
    }
  }

  async function handleLinkFundraising(activityId: string) {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/fundraising/${activityId}`, {
        method: 'POST',
      });
      setShowLinkActivity(false);
      await fetchMemberDetail(memberDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('Member is already linked to this fundraising activity');
      } else {
        setError(apiErr.message || 'Failed to link activity.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Affiliation Management ────────────────────────────────

  async function fetchAvailableSubcommittees() {
    try {
      const subs = await apiClient<SubcommitteeOption[]>('/subcommittees');
      setAvailableSubcommittees(subs);
    } catch {
      setAvailableSubcommittees([]);
    }
  }

  async function handleAddAffiliation() {
    if (!memberDetail || !newAffSubcommitteeId) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/subcommittees`, {
        method: 'POST',
        body: { subcommitteeId: newAffSubcommitteeId, role: newAffRole },
      });
      setNewAffSubcommitteeId('');
      setNewAffRole('Member');
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('Member is already affiliated with this subcommittee');
      } else {
        setError(apiErr.message || 'Failed to add affiliation.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemoveAffiliation(subcommitteeId: string) {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/subcommittees/${subcommitteeId}`, {
        method: 'DELETE',
      });
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to remove affiliation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateAffiliationRole(subcommitteeId: string, role: SubcommitteeRole) {
    if (!memberDetail) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}/subcommittees/${subcommitteeId}`, {
        method: 'PATCH',
        body: { role },
      });
      await fetchMemberDetail(memberDetail.id);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update affiliation role.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Delete ────────────────────────────────────────────────

  async function handleDeleteMember() {
    if (!memberDetail) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${memberDetail.firstName} ${memberDetail.lastName}? This action cannot be undone.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/members/${memberDetail.id}`, { method: 'DELETE' });
      setExpandedMemberId(null);
      setMemberDetail(null);
      await fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete member.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Computed Values ───────────────────────────────────────

  const duesSummary = computeDuesSummary(members, duesPeriods);
  const filtered = sortMembers(filterMembers(members, searchText, statusFilter, chapterFilter, subcommitteeFilter, executiveFilter, membershipLevelFilter));
  const uniqueChapters = Array.from(new Set(members.map(m => m.chapterAffiliation).filter(Boolean))) as string[];
  const uniqueSubcommittees = Array.from(
    new Map(
      members.flatMap(m => m.subcommitteeAffiliations).map(a => [a.subcommitteeId, a.subcommitteeName])
    ).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // ── Access Check ──────────────────────────────────────────

  if (!isAtLeast(user?.role || '', 'Organizer')) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Access Denied</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>KDU Georgia Members</h1>
        <div style={styles.loading}>Loading members...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>KDU Georgia Members</h1>

      {error && <div style={styles.error}>{error}</div>}

      {/* Dues Summary Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Dues Summary</h2>
        {!duesSummary.hasDuesPeriod ? (
          <p style={{ color: '#666', fontSize: '0.9rem' }}>No active dues period configured for the current year.</p>
        ) : (
          <>
            <div style={styles.summaryCards}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{duesSummary.totalMembers}</div>
                <div style={styles.summaryLabel}>Total Members</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{duesSummary.paidCount}</div>
                <div style={styles.summaryLabel}>Paid in Full</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{duesSummary.unpaidCount}</div>
                <div style={styles.summaryLabel}>Unpaid</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>${duesSummary.totalCollected.toFixed(2)}</div>
                <div style={styles.summaryLabel}>Total Collected</div>
              </div>
            </div>
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${Math.min(duesSummary.progressPercent, 100)}%`, backgroundColor: '#1a6b3c' }}>
                  {duesSummary.progressPercent > 10 ? `${duesSummary.progressPercent.toFixed(0)}%` : ''}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filter Bar */}
      <div style={styles.section}>
        <div style={styles.filterBar}>
          <input
            type="text"
            style={{ ...styles.input, maxWidth: '300px' }}
            placeholder="Search by name or email..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <select
            style={styles.select}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as MembershipStatus | '')}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </select>
          <select
            style={styles.select}
            value={chapterFilter}
            onChange={e => setChapterFilter(e.target.value)}
          >
            <option value="">All Chapters</option>
            {uniqueChapters.map(ch => (
              <option key={ch} value={ch}>{ch}</option>
            ))}
          </select>
          <select
            style={styles.select}
            value={subcommitteeFilter}
            onChange={e => setSubcommitteeFilter(e.target.value)}
          >
            <option value="">All Sub-Committees</option>
            {uniqueSubcommittees.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <select
            style={styles.select}
            value={executiveFilter}
            onChange={e => setExecutiveFilter(e.target.value as '' | 'yes' | 'no')}
          >
            <option value="">All Members</option>
            <option value="yes">Executives Only</option>
            <option value="no">Non-Executives</option>
          </select>
          <select
            style={styles.select}
            value={membershipLevelFilter}
            onChange={e => setMembershipLevelFilter(e.target.value)}
          >
            <option value="">All Levels</option>
            {membershipLevels.map(level => (
              <option key={level.id} value={level.id}>{level.name}</option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <button
              style={viewMode === 'cards' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('cards')}
            >Cards</button>
            <button
              style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('table')}
            >Table</button>
          </div>
        </div>

        {/* Add Member Button */}
        {canEdit && (
        <button style={styles.btn} onClick={() => setShowRegistrationForm(!showRegistrationForm)}>
          {showRegistrationForm ? 'Cancel' : 'Add Member'}
        </button>
        )}
      </div>

      {/* Registration Form */}
      {showRegistrationForm && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Register New Member</h2>
          {regErrors.length > 0 && (
            <div style={styles.error}>
              {regErrors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
          <form onSubmit={handleRegisterMember}>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>First Name *</label>
                <input style={styles.input} value={regFirstName} onChange={e => setRegFirstName(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Last Name *</label>
                <input style={styles.input} value={regLastName} onChange={e => setRegLastName(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input style={styles.input} type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={regPhone} onChange={e => setRegPhone(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Mailing Address</label>
                <input style={styles.input} value={regAddress} onChange={e => setRegAddress(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Chapter Affiliation</label>
                <input style={styles.input} value={regChapter} onChange={e => setRegChapter(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Status</label>
                <select style={styles.select} value={regStatus} onChange={e => setRegStatus(e.target.value as MembershipStatus)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Membership Level</label>
                <select
                  style={{ ...styles.select, width: '100%' }}
                  value={regMembershipLevelId}
                  onChange={e => setRegMembershipLevelId(e.target.value || '')}
                >
                  <option value="">— None —</option>
                  {membershipLevels.map(level => (
                    <option key={level.id} value={level.id}>{level.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Is Executive</label>
                <div style={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                  <input
                    type="checkbox"
                    checked={regIsExecutive}
                    onChange={e => {
                      const checked = e.target.checked;
                      setRegIsExecutive(checked);
                      if (!checked) setRegExecutiveTitleId('');
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#333' }}>
                    {regIsExecutive ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              {regIsExecutive && (
                <div style={styles.field}>
                  <label style={styles.label}>Executive Title</label>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={regExecutiveTitleId}
                    onChange={e => setRegExecutiveTitleId(e.target.value || '')}
                  >
                    <option value="">— Select Title —</option>
                    {executiveTitles.map(title => (
                      <option key={title.id} value={title.id}>{title.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button type="submit" style={styles.btn} disabled={submitting}>
                {submitting ? 'Saving...' : 'Register Member'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Member Directory */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Member Directory ({filtered.length})</h2>

        {filtered.length === 0 ? (
          <div style={styles.emptyState}>No members match the current filters.</div>
        ) : viewMode === 'table' ? (
          /* Table View */
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Chapter</th>
                  <th style={styles.th}>Executive</th>
                  <th style={styles.th}>Level</th>
                  <th style={styles.th}>Committees</th>
                  <th style={styles.th}>Paid</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Join Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(member => (
                  <React.Fragment key={member.id}>
                    <tr
                      style={{ ...styles.trClickable, ...(expandedMemberId === member.id ? styles.trExpanded : {}) }}
                      onClick={() => handleExpandMember(member.id)}
                    >
                      <td style={styles.td}>{member.firstName} {member.lastName}</td>
                      <td style={styles.td}>{member.email}</td>
                      <td style={styles.td}>{member.chapterAffiliation || '—'}</td>
                      <td style={styles.td}>
                        {member.isExecutive ? (
                          <span style={{ ...styles.badge, backgroundColor: '#c9a84c' }}>
                            {executiveTitles.find(t => t.id === member.executiveTitleId)?.name || 'Executive'}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={styles.td}>
                        {member.membershipLevelId
                          ? membershipLevels.find(l => l.id === member.membershipLevelId)?.name || '—'
                          : '—'}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {member.subcommitteeAffiliations && member.subcommitteeAffiliations.map(aff => (
                            <span
                              key={aff.subcommitteeId}
                              style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: '#fff',
                                backgroundColor: ROLE_COLORS[aff.role],
                              }}
                            >
                              {aff.subcommitteeName}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: member.paidStatus ? '#1a6b3c' : '#999', fontWeight: 600 }}>
                          {member.paidStatus ? '✓' : '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[member.status] }}>
                          {member.status}
                        </span>
                      </td>
                      <td style={styles.td}>{new Date(member.joinDate).toLocaleDateString()}</td>
                    </tr>
                    {expandedMemberId === member.id && (
                      <tr><td colSpan={9} style={{ padding: 0 }}>{renderDetailPanel()}</td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div style={styles.cardGrid}>
            {filtered.map(member => (
              <div key={member.id}>
                <div
                  style={{ ...styles.card, ...(expandedMemberId === member.id ? { backgroundColor: '#f0f7f2', borderColor: '#1a6b3c' } : {}) }}
                  onClick={() => handleExpandMember(member.id)}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{member.firstName} {member.lastName}</div>
                  {/* Affiliation Badges */}
                  {member.subcommitteeAffiliations && member.subcommitteeAffiliations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.35rem' }}>
                      {member.subcommitteeAffiliations.map(aff => (
                        <span
                          key={aff.subcommitteeId}
                          style={{
                            display: 'inline-block',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: ROLE_COLORS[aff.role],
                          }}
                        >
                          {aff.subcommitteeName}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>{member.email}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[member.status] }}>{member.status}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: member.paidStatus ? '#1a6b3c' : '#999' }}>
                        {member.paidStatus ? '✓ Paid' : '— Unpaid'}
                      </span>
                      {member.chapterAffiliation && <span style={{ fontSize: '0.8rem', color: '#666' }}>{member.chapterAffiliation}</span>}
                    </div>
                  </div>
                </div>
                {expandedMemberId === member.id && renderDetailPanel()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Detail Panel Renderer ─────────────────────────────────

  function renderDetailPanel() {
    if (detailLoading) {
      return <div style={styles.detailPanel}><div style={styles.loading}>Loading details...</div></div>;
    }
    if (!memberDetail) {
      return <div style={styles.detailPanel}><div style={styles.loading}>Loading...</div></div>;
    }

    return (
      <div style={styles.detailPanel}>
        {/* Profile Section */}
        <div style={styles.detailSection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#0f2d1f', fontSize: '1rem' }}>Profile</h3>
            <div>
              {canEdit && !editMode && (
                <>
                  <button style={styles.btn} onClick={enterEditMode}>Edit</button>
                  <button style={styles.btnDanger} onClick={handleDeleteMember}>Delete</button>
                </>
              )}
            </div>
          </div>

          {editMode ? (
            <form onSubmit={handleSaveEdit}>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>First Name</label>
                  <input style={styles.input} value={editFirstName} onChange={e => setEditFirstName(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Last Name</label>
                  <input style={styles.input} value={editLastName} onChange={e => setEditLastName(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Email</label>
                  <input style={styles.input} type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Mailing Address</label>
                  <input style={styles.input} value={editAddress} onChange={e => setEditAddress(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Chapter Affiliation</label>
                  <input style={styles.input} value={editChapter} onChange={e => setEditChapter(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.select} value={editStatus} onChange={e => setEditStatus(e.target.value as MembershipStatus)}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Membership Level</label>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={editMembershipLevelId || ''}
                    onChange={e => setEditMembershipLevelId(e.target.value || null)}
                  >
                    <option value="">— None —</option>
                    {membershipLevels.map(level => (
                      <option key={level.id} value={level.id}>{level.name}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Is Executive</label>
                  <div style={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                    <input
                      type="checkbox"
                      checked={editIsExecutive}
                      onChange={e => {
                        const checked = e.target.checked;
                        setEditIsExecutive(checked);
                        if (!checked) {
                          setEditExecutiveTitleId(null);
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#333' }}>
                      {editIsExecutive ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                {editIsExecutive && (
                  <div style={styles.field}>
                    <label style={styles.label}>Executive Title</label>
                    <select
                      style={{ ...styles.select, width: '100%' }}
                      value={editExecutiveTitleId || ''}
                      onChange={e => setEditExecutiveTitleId(e.target.value || null)}
                    >
                      <option value="">— Select Title —</option>
                      {executiveTitles.map(title => (
                        <option key={title.id} value={title.id}>{title.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" style={styles.btn} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
                <button type="button" style={styles.btnSecondary} onClick={cancelEditMode}>Cancel</button>
              </div>
            </form>
          ) : (
            <div style={styles.detailGrid}>
              <div><div style={styles.detailLabel}>First Name</div><div style={styles.detailValue}>{memberDetail.firstName}</div></div>
              <div><div style={styles.detailLabel}>Last Name</div><div style={styles.detailValue}>{memberDetail.lastName}</div></div>
              <div><div style={styles.detailLabel}>Email</div><div style={styles.detailValue}>{memberDetail.email}</div></div>
              <div><div style={styles.detailLabel}>Phone</div><div style={styles.detailValue}>{memberDetail.phone || '—'}</div></div>
              <div><div style={styles.detailLabel}>Mailing Address</div><div style={styles.detailValue}>{memberDetail.mailingAddress || '—'}</div></div>
              <div><div style={styles.detailLabel}>Chapter</div><div style={styles.detailValue}>{memberDetail.chapterAffiliation || '—'}</div></div>
              <div><div style={styles.detailLabel}>Join Date</div><div style={styles.detailValue}>{new Date(memberDetail.joinDate).toLocaleDateString()}</div></div>
              <div><div style={styles.detailLabel}>Status</div><div style={styles.detailValue}><span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[memberDetail.status] }}>{memberDetail.status}</span></div></div>
              <div><div style={styles.detailLabel}>Membership Level</div><div style={styles.detailValue}>{memberDetail.membershipLevel?.name || '—'}</div></div>
              <div><div style={styles.detailLabel}>Executive</div><div style={styles.detailValue}>{memberDetail.isExecutive ? (memberDetail.executiveTitle?.name || 'Yes') : 'No'}</div></div>
            </div>
          )}
        </div>

        {/* Dues Section */}
        <div style={styles.detailSection}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>Dues</h3>
          {memberDetail.duesStatus ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div><div style={styles.detailLabel}>Annual Amount</div><div style={styles.detailValue}>${memberDetail.duesStatus.annualAmount.toFixed(2)}</div></div>
                <div><div style={styles.detailLabel}>Total Paid</div><div style={styles.detailValue}>${memberDetail.duesStatus.totalPaid.toFixed(2)}</div></div>
                <div><div style={styles.detailLabel}>Remaining</div><div style={styles.detailValue}>${memberDetail.duesStatus.remainingBalance.toFixed(2)}</div></div>
                <div><div style={styles.detailLabel}>Status</div><div style={styles.detailValue}>{memberDetail.duesStatus.paidInFull ? '✅ Paid in Full' : '⏳ Outstanding'}</div></div>
              </div>
            </>
          ) : (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>No active dues period. Payment recording is disabled.</p>
          )}

          {/* Payment History */}
          {memberDetail.duesPayments && memberDetail.duesPayments.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Payment History</div>
              {memberDetail.duesPayments.map(payment => (
                <div key={payment.id} style={styles.paymentItem}>
                  <span style={{ fontWeight: 600 }}>${payment.amount.toFixed(2)}</span>
                  {' — '}
                  <span>{new Date(payment.paymentDate).toLocaleDateString()}</span>
                  {' — '}
                  <span style={{ color: '#666' }}>{payment.paymentMethod}</span>
                  {payment.duesPeriod && <span style={{ color: '#999', marginLeft: '0.5rem' }}>({payment.duesPeriod.year})</span>}
                </div>
              ))}
            </div>
          )}

          {/* Record Payment */}
          {memberDetail.duesStatus && !showPaymentForm && (
            <button style={styles.btnGold} onClick={() => { setShowPaymentForm(true); setPayPeriodId(memberDetail.duesStatus?.periodId || ''); }}>
              Record Payment
            </button>
          )}
          {showPaymentForm && (
            <form onSubmit={handleRecordPayment} style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>Amount</label>
                  <input style={styles.input} type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Payment Date</label>
                  <input style={styles.input} type="date" value={payDate} onChange={e => setPayDate(e.target.value)} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Payment Method</label>
                  <select style={styles.select} value={payMethod} onChange={e => setPayMethod(e.target.value as PaymentMethod)}>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Dues Period</label>
                  <select style={styles.select} value={payPeriodId} onChange={e => setPayPeriodId(e.target.value)}>
                    <option value="">Select period</option>
                    {duesPeriods.map(p => <option key={p.id} value={p.id}>{p.year} (${p.annualAmount})</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button type="submit" style={styles.btn} disabled={submitting}>{submitting ? 'Saving...' : 'Submit Payment'}</button>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowPaymentForm(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>

        {/* User Link Section */}
        <div style={styles.detailSection}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>Linked User Account</h3>
          {memberDetail.user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{memberDetail.user.fullName}</div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>{memberDetail.user.email}</div>
              </div>
              <button style={styles.btnDanger} onClick={handleUnlinkUser} disabled={submitting}>
                Unlink
              </button>
              <button style={styles.btn} onClick={handleSyncUser} disabled={submitting} title="Update User email & name to match this Member">
                Sync to User
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>No user account linked. Search to link one:</p>
              <UserSearchDropdown onSelect={(userId) => handleLinkUser(userId)} />
            </div>
          )}
        </div>

        {/* Subcommittee Affiliations Section */}
        <div style={styles.detailSection}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>Subcommittee Affiliations</h3>

          {/* Existing Affiliations */}
          {memberDetail.subcommitteeAffiliations && memberDetail.subcommitteeAffiliations.length > 0 ? (
            <div style={{ marginBottom: '1rem' }}>
              {memberDetail.subcommitteeAffiliations.map(aff => (
                <div key={aff.subcommitteeId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>{aff.subcommitteeName}</span>
                  <select
                    style={{ ...styles.select, fontSize: '0.8rem', padding: '0.3rem 0.5rem' }}
                    value={aff.role}
                    onChange={e => handleUpdateAffiliationRole(aff.subcommitteeId, e.target.value as SubcommitteeRole)}
                  >
                    <option value="Chair">Chair</option>
                    <option value="Member">Member</option>
                    <option value="Volunteer">Volunteer</option>
                  </select>
                  <button
                    style={{ ...styles.btnDanger, fontSize: '0.75rem', padding: '0.3rem 0.6rem', marginRight: 0 }}
                    onClick={() => handleRemoveAffiliation(aff.subcommitteeId)}
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>No subcommittee affiliations.</p>
          )}

          {/* Add Affiliation Form */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <select
              style={styles.select}
              value={newAffSubcommitteeId}
              onChange={e => setNewAffSubcommitteeId(e.target.value)}
              onFocus={() => { if (availableSubcommittees.length === 0) fetchAvailableSubcommittees(); }}
            >
              <option value="">Select subcommittee...</option>
              {availableSubcommittees.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>
            <select
              style={styles.select}
              value={newAffRole}
              onChange={e => setNewAffRole(e.target.value as SubcommitteeRole)}
            >
              <option value="Chair">Chair</option>
              <option value="Member">Member</option>
              <option value="Volunteer">Volunteer</option>
            </select>
            <button
              style={styles.btn}
              onClick={handleAddAffiliation}
              disabled={submitting || !newAffSubcommitteeId}
            >
              Add
            </button>
          </div>
        </div>

        {/* Fundraising Section */}
        <div style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>Fundraising Participation</h3>
          {memberDetail.fundraisingLinks && memberDetail.fundraisingLinks.length > 0 ? (
            <div style={{ marginBottom: '1rem' }}>
              {memberDetail.fundraisingLinks.map(link => (
                <div key={link.id} style={styles.paymentItem}>
                  <span style={{ fontWeight: 600 }}>{link.name}</span>
                  {' — '}
                  <span style={{ color: '#666' }}>{link.type}</span>
                  {' — '}
                  <span style={{ color: '#999' }}>{new Date(link.associationDate).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>No fundraising activities linked.</p>
          )}

          {!showLinkActivity ? (
            <button style={styles.btnGold} onClick={handleShowLinkActivity}>Link Activity</button>
          ) : (
            <div style={{ marginTop: '0.5rem' }}>
              <select
                style={styles.select}
                onChange={e => { if (e.target.value) handleLinkFundraising(e.target.value); }}
                defaultValue=""
              >
                <option value="" disabled>Select an activity...</option>
                {fundraisingActivities.map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                ))}
              </select>
              <button style={{ ...styles.btnSecondary, marginLeft: '0.5rem' }} onClick={() => setShowLinkActivity(false)}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
